import React, { Fragment, useState, useEffect } from "react";
import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup";
import { AvForm, AvGroup, AvField } from "availity-reactstrap-validation";
import { Col, Label, CardHeader } from "reactstrap";
import { useListContext } from "contextAPI/ListsContext";
import { regexNameList } from "utils/regexExpressions";
import { loaderElement } from "utils/loaderElement";
import { enumsCreateList, enumsListForm } from "utils/enums";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import Select from "react-select";
import useList from "hooks/useList";
import usePaginatedSelect from "customsHooks/usePaginatedSelect";

const CreateListForm = () => {
  const { t } = useTranslation();
  const {
    setCanNext,
    createListState,
    setCreateListState,
    setErrorList,
  } = useListContext();

  const { getLists } = useList();

  const {
    options: listOptions,
    isLoading: isLoadingLists,
    loadInitial: loadInitialLists,
    loadInitialDebounced: loadInitialListsDebounced,
    loadMore: loadMoreLists,
    resetSearch: resetSearchLists,
  } = usePaginatedSelect({
    fetchFn: (page, perPage, search) => getLists(page, perPage, search),
    formatFn: (item) => ({ value: item.uuid, label: item.name }),
    filterFn: (item) => item.status === enumsListForm.LIST_ACTIVE,
  });

  const [selectedParentOption, setSelectedParentOption] = useState(null);

  let placeholderParentList = null;

  /**
   * Handles key press events, preventing the default behavior if the pressed key is the "Enter" key.
   * @param {object} eventKeyPress - The key press event object.
   * @param {string} eventKeyPress.key - The key that was pressed.
   * @param {Function} eventKeyPress.preventDefault - A function to prevent the default behavior of the key press event.
   * @returns {void}
   */
  const handleOnKeyPress = (eventKeyPress) => {
    if (eventKeyPress.key === "Enter") {
      eventKeyPress.preventDefault();
    }
  };

  /**
   * Handles the onBlur event for the name input field of a list creation form.
   * @param {Event} eventBlurListName - The onBlur event object for the name input field.
   * @returns {void}
   */
  const handleOnBlurNameList = (eventBlurListName) => {
    setCreateListState({
      ...createListState,
      name: eventBlurListName.target.value.trim(),
    });
  };

  /**
   * Handles the change event for updating the name of a list.
   * @param {Event} eventChangeListName - The change event object containing information about the list name change.
   * @param {object} createListState - The current state object representing the list being created.
   * @param {string} createListState.name - The name of the list being created.
   * @param {function} setCreateListState - The state setter function to update the state of the list being created.
   * @returns {void}
   */
  const handleOnChangeNameList = (eventChangeListName) => {
    setCreateListState({
      ...createListState,
      name: eventChangeListName.target.value,
    });
  };

  /**
   * Handles the change event of the parent list selection.
   * @param {object} eventChangeParentList - The event object representing the change in the parent list selection.
   * @param {string} eventChangeParentList.target.value - The value selected in the parent list.
   * @returns {void}
   */
  const handleOnChangeParentList = (option) => {
    setSelectedParentOption(option);
    if (option) {
      setCreateListState({
        ...createListState,
        parent_id: option.value,
      });
    } else {
      const updatedListForm = { ...createListState };
      delete updatedListForm.parent_id;
      setCreateListState(updatedListForm);
    }
  };

  /**
   * Handles the menu close event for the parent list combo box.
   * Resets the search term and reloads the full list of options from cache or API.
   * @returns {void}
   */
  const handleMenuCloseLists = () => {
    resetSearchLists();
    loadInitialLists();
  };

  /**
   * Executes an effect to retrieve lists by search and reset error state when the component mounts.
   * @returns {void}
   */
  useEffect(() => {
    loadInitialLists();
    setErrorList(false);
  }, []);

  /**
   * useEffect hook that checks the validity of the name in the createListState object
   * and updates the state to enable or disable the next action accordingly.
   * @param {object} createListState - The state object containing the name to be validated.
   * @param {string} createListState.name - The name to be validated.
   * @param {boolean} setCanNext - State setter function for enabling or disabling the next action.
   * @returns {void}
   */
  useEffect(() => {
    if (
      !createListState.name ||
      createListState.name.length < 2 ||
      createListState.name.length > 100 ||
      !regexNameList.test(createListState.name)
    ) {
      setCanNext(true);
    } else {
      setCanNext(false);
    }
  }, [createListState.name]);

  /**
   * Determines the placeholder text for the parent list selection based on the loading state of the lists.
   * If the lists are currently loading, the placeholder will indicate that loading is in progress.
   * Otherwise, it will display a default message prompting the user to select a parent list.
   * @param {boolean} isLoadingLists - The flag indicating whether the lists are currently loading.
   * @returns {string} The appropriate placeholder text based on the loading state of the lists.
   */
  if (isLoadingLists === true) {
    placeholderParentList = t("loading");
  } else {
    placeholderParentList = t("form.designerLabel10.1");
  }

  return (
    <Fragment>
      {loaderElement(isLoadingLists)}
      <CSSTransitionGroup
        component="div"
        transitionName="TabsAnimation"
        transitionAppear={true}
        transitionAppearTimeout={0}
        transitionEnter={false}
        transitionLeave={false}
      >
        <div>
          <AvForm className="mb-2">
            <CardHeader className="mb-4">{t("list.designerItem2")}</CardHeader>

            <AvGroup row>
              <ReactTooltip id="name_list" place="top" type="info" effect="solid">
                {t("trd.Tooltip4")}
              </ReactTooltip>

              <Label for="list_name" className="is-required" sm={3}>
                {t("list.designerLabel1")}
              </Label>
              <Col md={9}>
                <AvField
                  id="name_list"
                  name="name_list"
                  type="text"
                  data-test-id="name-list"
                  data-tip
                  data-for="name_list"
                  autoComplete="off"
                  onChange={handleOnChangeNameList}
                  onBlur={handleOnBlurNameList}
                  onKeyPress={handleOnKeyPress}
                  value={createListState.name}
                  validate={{
                    required: {
                      value: true,
                      errorMessage: `${t("fieldRequired")}`,
                    },
                    pattern: {
                      value: regexNameList,
                      errorMessage: `${t("list.designerFeedback1")}`,
                    },
                    minLength: {
                      value: enumsCreateList.MIN_LENGTH_NAME_LIST,
                      errorMessage: `${t("fieldValidateLengthBetween")} 2 ${t(
                        "and"
                      )} 100 ${t("characters")}`,
                    },
                    maxLength: {
                      value: enumsCreateList.MAX_LENGTH_NAME_LIST,
                      errorMessage: `${t("fieldValidateLengthBetween")} 2 ${t(
                        "and"
                      )} 100 ${t("characters")}`,
                    },
                  }}
                />
              </Col>
            </AvGroup>

            <AvGroup row>
              <Label for="parent_List" sm={3}>
                {t("list.designerLabel2")}
              </Label>
              <Col md={9}>
                <Select
                  id="parent_List"
                  name="parent_List"
                  placeholder={placeholderParentList}
                  value={selectedParentOption}
                  options={listOptions}
                  onChange={handleOnChangeParentList}
                  onMenuClose={handleMenuCloseLists}
                  onMenuScrollToBottom={loadMoreLists}
                  onInputChange={(inputValue, action) => {
                    if (action.action === "input-change") {
                      loadInitialListsDebounced(inputValue);
                    }
                  }}
                  filterOption={null}
                  isLoading={isLoadingLists}
                  noOptionsMessage={() => t("form.designerLabel10.1")}
                  isClearable
                />
              </Col>
            </AvGroup>
          </AvForm>
        </div>
      </CSSTransitionGroup>
    </Fragment>
  );
};

export default CreateListForm;
